import React, { useState } from "react";
import {
  Avatar,
  Box,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
  Button,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useStyles } from "../../assets/styles/Theme.tsx";
import {
  ExitToApp,
  LockOutlined,
  PersonOutlined,
  OpenInNew,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import SystemAlert from "./SystemAlert.tsx";
import { useDispatch } from "react-redux";
import { resetUserParams } from "../../store/ReduxUser.tsx";
import { clearAccessRights } from "../../store/ReduxAccessRight.tsx";
import { clearAccessModules } from "../../store/ReduxAccessModules.tsx";

// Types and Interfaces
interface App {
  name: string;
  description: string;
  logo: string;
}

interface MenuItem {
  name: string;
  icon: React.ReactElement;
}

interface SnackbarState {
  open: boolean;
  message: string;
  type: "success" | "error" | "warning" | "info";
}

interface CustomTheme {
  palette: {
    primary: {
      main: string;
    };
    secondary: {
      main: string;
    };
    tertiary?: {
      main: string;
    };
    background: {
      default: string;
      paper: string;
    };
    text: {
      primary: string;
      secondary: string;
    };
    greyscale?: {
      white?: string;
    };
    button?: {
      primaryText?: string;
    };
  };
  typography: {
    subtitle_m?: {
      fontSize: string;
      fontWeight: number;
    };
    subtitle_s?: {
      fontSize: string;
      fontWeight: number;
    };
    body_s?: {
      fontSize: string;
    };
    body_xs?: {
      fontSize: string;
    };
    button_s?: {
      fontSize: string;
      fontWeight: number;
    };
  };
}

const apps: App[] = [
  {
    name: "ESKAIY POS",
    description: "Point of Sales Software",
    logo: "https://storage.googleapis.com/a1aa/image/Y-zqZDr04s0vrd6jDPQfW_MGq2vA1zkTjwcgEZU3Ljg.jpg",
  },
  {
    name: "ESKAIY RES",
    description: "Restaurant Management Software",
    logo: "https://storage.googleapis.com/a1aa/image/MmCcbJfLQYNpfiHNzwgs9UKXjdIzzE1MsdspN8CbmZ4.jpg",
  },
  {
    name: "ESKAIY ITEM MANAGEMENT",
    description: "Item Management Software",
    logo: "https://storage.googleapis.com/a1aa/image/TMhsfHFnD21TFaij6iDI5h3qn-b6S_s-oNzL94mSiJk.jpg",
  },
  {
    name: "ESKAIY PMS",
    description: "Property Management Software",
    logo: "https://storage.googleapis.com/a1aa/image/_geXj7xAoZnzrJ7iqdam03xAv4JtjUswYS4qkKNSByM.jpg",
  },
];

const menuItems: MenuItem[] = [
  { name: "Profile", icon: <PersonOutlined /> },
  { name: "Change Password", icon: <LockOutlined /> },
  { name: "Log Out", icon: <ExitToApp /> },
];

const Profile: React.FC = () => {
  const theme = useTheme() as CustomTheme;
  const styles = useStyles(theme);
  const [selectedApp, setSelectedApp] = useState<string>(apps[0].name);
  const [selectedMenuItem, setSelectedMenuItem] = useState<string>("");
  const dispatch = useDispatch();
  const [snackbar, setSnackbar] = useState<SnackbarState>({
    open: false,
    message: "",
    type: "success",
  });

  const navigate = useNavigate();

  const handleLogout = (): void => {
    // Optional: Clear localStorage/session etc.
    localStorage.clear();
    sessionStorage.clear();

    dispatch(resetUserParams());
    dispatch(clearAccessRights());
    dispatch(clearAccessModules());

    setSnackbar({
      open: true,
      message: "Logged Out Successfully!",
      type: "success",
    });

    setTimeout(() => {
      navigate("/");
    }, 2000);
  };

  return (
    <Box
      sx={{
        width: 380,
        bgcolor: "background.paper",
        borderRadius: 1,
        boxShadow: 4,
        overflow: "hidden",
        p: 2,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
        <Avatar
          alt="User avatar"
          src="https://storage.googleapis.com/a1aa/image/sEOZWreQ55qwQmRvi1gGJdVCTxq404PM_Rpw9wDBMug.jpg"
          sx={{ width: 48, height: 48 }}
        />
        <Box sx={{ ml: 2, display: "flex", flexDirection: "column" }}>
          <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 500 }}>
            John Doe
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
            Admin
          </Typography>
        </Box>
        <Box
          sx={{
            ml: "auto",
            textAlign: "right",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
            Default Location
          </Typography>
          <Typography variant="button" color="primary" sx={{ fontSize: '0.875rem', fontWeight: 500 }}>
            Item Management Colombo
          </Typography>
        </Box>
      </Box>
      <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ fontSize: '0.875rem', fontWeight: 500 }}>
        Your ESKAIY Apps
      </Typography>
      <Box
        sx={{
          mb: 2,
          maxHeight: 300,
          overflowY: "auto",
          backgroundColor: theme.palette.background.default,
          p: 1,
          borderRadius: 1,
        }}
      >
        {apps.map((app: App) => (
          <Box
            key={app.name}
            sx={{
              display: "flex",
              alignItems: "center",
              bgcolor:
                selectedApp === app.name
                  ? theme.palette.primary.main
                  : theme.palette.background.paper,
              boxShadow: `0px 1px 2px rgba(0, 0, 0, 0.1)`,
              p: 2,
              borderRadius: 1,
              mb: 1,
              cursor: "pointer",
              "&:hover": {
                bgcolor: "rgba(25, 118, 210, 0.04)",
              },
            }}
            onClick={() => setSelectedApp(app.name)}
          >
            <Avatar
              alt={`${app.name} logo`}
              src={app.logo}
              sx={{ width: 40, height: 40 }}
            />
            <Box sx={{ ml: 2, display: "flex", flexDirection: "column" }}>
              <Typography
                variant="button"
                sx={{
                  color:
                    selectedApp === app.name
                      ? theme.palette.greyscale?.white || '#FFFFFF'
                      : theme.palette.text.primary,
                  fontSize: '0.875rem',
                  fontWeight: 500,
                }}
              >
                {app.name}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color:
                    selectedApp === app.name
                      ? theme.palette.greyscale?.white || '#FFFFFF'
                      : theme.palette.text.secondary,
                  fontSize: '0.75rem',
                }}
              >
                {app.description}
              </Typography>
            </Box>
            <IconButton
              sx={{
                ml: "auto",
                color:
                  selectedApp === app.name
                    ? theme.palette.greyscale?.white || '#FFFFFF'
                    : theme.palette.text.primary,
              }}
            >
              <OpenInNew />
            </IconButton>
          </Box>
        ))}
      </Box>

      <Divider />
      <List>
        {menuItems.map((item: MenuItem) => (
          <ListItem
            key={item.name}
            sx={{
              transition: "all 0.3s ease-in-out",
              backgroundColor:
                selectedMenuItem === item.name
                  ? theme.palette.secondary.main
                  : "inherit",
              "&:hover": {
                backgroundColor: theme.palette.tertiary?.main || '#f5f5f5',
                color: theme.palette.primary.main,
                borderRight: `3px solid ${theme.palette.secondary.main}`,
              },
              boxShadow:
                selectedMenuItem === item.name
                  ? `0px 1px 2px rgba(0, 0, 0, 0.1)`
                  : 0,
              color:
                selectedMenuItem === item.name
                  ? theme.palette.button?.primaryText || '#FFFFFF'
                  : theme.palette.text.primary,
              gap: 2,
              cursor: "pointer",
            }}
            onClick={() => {
              setSelectedMenuItem(item.name);
              if (item.name === "Log Out") {
                handleLogout();
              }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {item.icon}
              <ListItemText
                primary={item.name}
                primaryTypographyProps={{
                  fontSize: theme.typography.body_s?.fontSize || '0.875rem',
                }}
              />
            </Box>
          </ListItem>
        ))}
      </List>
      <SystemAlert
        open={snackbar.open}
        type={snackbar.type}
        message={snackbar.message}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      />
    </Box>
  );
};

export default Profile;